import {Image, MediaFile, useProductOptions} from '@shopify/hydrogen';
import {
  MediaContentType,
  MediaImage,
} from '@shopify/hydrogen/storefront-api-types';
import useEmblaCarousel from 'embla-carousel-react';
import {useCallback, useEffect, useState} from 'react';
import type {ProductWithNodes} from '../../types';
import CircleButton from '../elements/CircleButton';
import {ArrowRightIcon} from '../icons/ArrowRight';
import clsx from 'clsx';
import {SanityProductPage} from '../../types';
import SanityImage from '../media/SanityImage.client';
import sanityConfig from '../../../sanity.config';
import play from './../../assets/images/play_1-1.svg';

/**
 * A client component that defines a media gallery for hosting images, 3D models, and videos of products
 */

type Props = {
  storefrontProduct: ProductWithNodes;
  sanityProduct: SanityProductPage;
};

export default function ProductGallery({
  storefrontProduct,
  sanityProduct,
}: Props) {
  const media = storefrontProduct?.media?.nodes;
  const {selectedVariant} = useProductOptions();
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [emblaRef, emblaApi] = useEmblaCarousel({
    align: 'start',
    draggable: media && media.length > 1,
    loop: true,
    skipSnaps: true,
    speed: 7,
  });

  const handleNext = () => {
    if (emblaApi) emblaApi.scrollNext();
  };

  const handlePrevious = () => {
    if (emblaApi) emblaApi.scrollPrev();
  };

  const handleScrollTo = (galleryIndex: number) => {
    if (emblaApi) emblaApi.scrollTo(galleryIndex, false);
  };

  const onSelect = useCallback(() => {
    if (!emblaApi) return;
    setSelectedIndex(emblaApi.selectedScrollSnap());
  }, [emblaApi, setSelectedIndex]);

  useEffect(() => {
    if (!emblaApi) return;
    emblaApi.on('select', onSelect);
  }, [emblaApi, onSelect]);

  useEffect(() => {
    if (!selectedVariant) {
      return;
    }

    const variantImageUrl = selectedVariant?.image?.url?.split('?')[0];
    const galleryIndex =
      media?.findIndex((mediaItem) => {
        if (mediaItem.mediaContentType === MediaContentType.Image) {
          return (
            (mediaItem as MediaImage)?.image?.url.split('?')[0] ===
            variantImageUrl
          );
        }
        return false;
      }) ?? -1;

    if (emblaApi && galleryIndex >= 0) {
      emblaApi.scrollTo(galleryIndex, true); // instantly scroll
    }
  }, [emblaApi, media, selectedVariant]);

  if (!media?.length) {
    return null;
  }

  return (
    <div
      className={clsx(
        'grid grid-cols-1 md:grid-cols-2',
        'mt-[70px] bg-white',
        'md:height-hero',
      )}
    >
      <div className={'md:height-hero relative'} tabIndex={-1}>
        <div className="h-full overflow-hidden" ref={emblaRef}>
          <div className="flex h-full">
            {/* Slides */}
            {media.map((med) => {
              return (
                <MediaFile
                  // @ts-ignore
                  className={clsx(
                    'relative flex w-full min-w-full select-none object-contain',
                    med?.mediaContentType === 'VIDEO'
                      ? 'h-[calc(100%-130px)]'
                      : 'aspect-square h-full object-contain md:h-[82%]',
                  )}
                  data={med}
                  draggable={false}
                  fetchpriority="high"
                  key={med.id}
                  options={{crop: 'center'}}
                  tabIndex={0}
                  alt={med.alt ?? ''}
                />
              );
            })}
          </div>
        </div>

        {/* Navigation */}
        <div className="absolute bottom-8 left-10 hidden gap-2 md:flex md:items-center">
          {media.length > 1 &&
            media.map((med, index) => {
              return (
                <div
                  role="button"
                  tabIndex={0}
                  className={clsx(
                    'group aspect-square h-16 overflow-hidden rounded bg-white shadow-xl transition-all duration-500',
                    selectedIndex === index ? 'h-20' : '', // Replace with active index
                    med?.mediaContentType === 'VIDEO'
                      ? 'rounded bg-lightGray'
                      : '',
                  )}
                  onClick={() => handleScrollTo(index)}
                  key={med.id}
                >
                  {med?.mediaContentType === 'VIDEO' && (
                    <Image
                      src={play}
                      width={100}
                      height={'auto'}
                      className={'h-full'}
                      alt={'Video'}
                    />
                  )}
                  {med?.mediaContentType !== 'VIDEO' && (
                    <MediaFile
                      // @ts-ignore
                      className={clsx(
                        'relative flex aspect-square h-full w-full object-cover',
                      )}
                      data={med}
                      draggable={false}
                      fetchpriority="low"
                      key={med.id}
                      options={{crop: 'center'}}
                      tabIndex={0}
                      alt={'Image thumb #' + (index + 1)}
                    />
                  )}
                </div>
              );
            })}
        </div>

        {/* Navigation Mobile */}
        <div
          className={clsx(
            'absolute bottom-4 left-4 flex w-[calc(100%-2rem)] gap-3',
            'justify-between md:bottom-[50%] md:left-4 md:right-4',
          )}
        >
          <CircleButton onClick={handlePrevious}>
            <ArrowRightIcon className="rotate-180" />
          </CircleButton>
          <CircleButton onClick={handleNext}>
            <ArrowRightIcon />
          </CircleButton>
        </div>
      </div>
      <picture className={clsx('height-hero relative hidden md:block')}>
        {sanityProduct.heroImage && (
          <SanityImage
            crop={sanityProduct.heroImage?.crop}
            dataset={sanityConfig.dataset}
            hotspot={sanityProduct.heroImage?.hotspot}
            layout="responsive"
            projectId={sanityConfig.projectId}
            sizes="100vw"
            src={sanityProduct.heroImage?.asset._ref}
            className={'h-full w-full object-cover'}
          />
        )}
      </picture>
    </div>
  );
}
